import {
  FormControl,
  FormHelperText,
  FormLabel,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { ReactNode } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "app/shadcnComponents/ui/select";

// Radix's Select.Item forbids an empty-string value, so the "no selection"
// option is tracked under this sentinel and translated back to "" at the edges.
const NONE_VALUE = "__NONE__";

export type StyledDropDownProps = {
  validationErrorMessage?: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  value?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  onChange: (e: SelectChangeEvent<unknown>) => void;
  options: {
    id: string;
    name: string;
    displayName: string;
    startIcon?: ReactNode;
  }[];
  inputLabel?: string;
};

export const StyledDropDown = (props: StyledDropDownProps) => {
  const handleValueChange = (value: string) => {
    props.onChange({
      target: { value: value === NONE_VALUE ? "" : value, name: props.name },
    } as unknown as SelectChangeEvent<unknown>);
  };

  return (
    <FormControl
      fullWidth
      disabled={props.disabled}
      required={props.required}
      error={!!__isError(props)}
    >
      {props.label && (
        <FormLabel>
          <Typography variant="formLabel">{props.label}</Typography>
        </FormLabel>
      )}
      {props.inputLabel && (
        <Typography
          variant="body2"
          className="text-teamApp-dropdown-text"
          sx={{ marginTop: ".5rem" }}
        >
          {props.inputLabel}
        </Typography>
      )}

      <Select
        value={props.value || NONE_VALUE}
        onValueChange={handleValueChange}
        disabled={props.disabled}
        name={props.name}
      >
        <SelectTrigger className="h-10 w-full gap-2 rounded-xl border-[#D7DEF8] bg-white px-3.5 py-2 text-sm text-[#002154] font-normal">
          {props.startAdornment}
          <SelectValue />
          {props.endAdornment}
        </SelectTrigger>
        <SelectContent className="z-[1400]">
          {props.options.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              <span className="inline-flex items-center gap-2">
                {option.startIcon}
                {option.displayName}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {__isError(props) ? (
        <FormHelperText variant="standard">
          {props.validationErrorMessage}
        </FormHelperText>
      ) : (
        <></>
      )}
    </FormControl>
  );
};

function __isError(props: StyledDropDownProps) {
  return (
    props.validationErrorMessage &&
    props.validationErrorMessage.length > 1 &&
    props.validationErrorMessage !== "Required."
  );
}
